import * as LabelPrimitive from "@radix-ui/react-label";
import { cn } from "cn";
import * as React from "react";

/**
 * The props FieldWrapper injects into its single child via cloneElement.
 * ANY component rendered directly inside a FieldWrapper must accept these and
 * forward them onto the element that carries the widget role — otherwise the
 * label's htmlFor dangles and the invalid outline never renders.
 */
export type FieldWrapperInjectedProps = {
  id?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-labelledby"?: string;
};

export type FieldWrapperProps = {
  children: React.ReactNode;
  label?: React.ReactNode;
  error?: string;
  optional?: boolean;
  /**
   * An action for the field, rendered at the right end of the label row (after
   * the optional marker) — e.g. a button that fills the field. It sits beside
   * the `<label>`, not inside it, so activating it does not focus the control.
   * Rendered only when `label` is.
   */
  labelAction?: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
  disabled?: boolean;
};

export function FieldWrapper({
  children,
  label,
  error,
  optional,
  labelAction,
  description,
  className,
  disabled,
}: FieldWrapperProps) {
  const id = React.useId();
  const errorId = `${id}-error`;
  const descriptionId = `${id}-description`;
  const labelId = `${id}-label`;

  const effectiveError = disabled ? undefined : error;
  // Only reference ids that are actually rendered — a dangling id makes screen
  // readers announce nothing at all.
  const describedBy =
    [description ? descriptionId : null, effectiveError ? errorId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  return (
    <div data-slot="form-item" className={cn(className)}>
      <div className="grid gap-1">
        {label && (
          <div className="flex flex-wrap items-baseline gap-1">
            <LabelPrimitive.Root
              data-slot="form-label"
              id={labelId}
              data-error={!!effectiveError}
              className={cn(
                /* sorted */
                "flex-1 min-w-0",
                "text-sm text-text-dark-high",
                "peer-disabled:cursor-not-allowed peer-disabled:opacity-70 data-[error=true]:text-text-negative",
              )}
              htmlFor={id}
            >
              {label}
            </LabelPrimitive.Root>
            {optional ? (
              <span className="text-text-dark-min text-sm leading-5 font-normal">
                (Optional)
              </span>
            ) : null}
            {labelAction}
          </div>
        )}

        {React.isValidElement(children)
          ? React.cloneElement(children as React.ReactElement<any>, {
              id,
              "aria-invalid": !!effectiveError,
              "aria-describedby": describedBy,
              // Only when a label actually renders — `aria-labelledby` pointing
              // at a missing node makes the widget nameless.
              "aria-labelledby": label ? labelId : undefined,
            })
          : children}
      </div>

      {description && (
        <p
          data-slot="form-description"
          id={descriptionId}
          className="text-text-dark-low text-sm mt-2"
        >
          {description}
        </p>
      )}

      {Boolean(effectiveError) && (
        <p
          data-slot="form-message"
          id={errorId}
          role="alert"
          className="text-text-negative text-xs mt-1"
        >
          {effectiveError}
        </p>
      )}
    </div>
  );
}
